/**
 * FileContextService — on-demand AI context for stored files.
 *
 * Historically `FilePanel.uploadFiles()` ran `fileProcessingService.extractContext()`
 * against every uploaded file, which meant one OpenAI Vision call per image (and one
 * summarisation call per long .csv/.json/.yaml) fired at upload time whether or not
 * anyone ever read the result. That call is billable, so it must not fire unbidden.
 *
 * This service moves the work to the moment a consumer actually needs the context and
 * guarantees that it happens at most once per file:
 *
 *   • `ensureContext()` returns the cached context if we already have one.
 *   • Otherwise it checks storage for a previously persisted context (a file analysed
 *     in an earlier session never pays again).
 *   • Otherwise it runs exactly one analysis. Concurrent callers share the same
 *     in-flight promise, so two consumers can never bill two calls.
 *
 * Failures are *not* swallowed. `FileProcessingService` catches its own errors and
 * returns a degraded stub (`processingMethod: 'fallback'`) that looks like a real
 * analysis to every downstream consumer and would be persisted forever. This service
 * treats that stub as a failure: it rejects, does not cache, and does not persist —
 * so the consumer can show a real error and the user can retry.
 */

import { fileStorageService } from './FileStorageService';
import { fileProcessingService } from './FileProcessingService';
import type { ProcessedContext } from './FileProcessingService';
import logger from '../utils/logger';

/** A stored context, tagged with the file it belongs to. */
export interface StoredFileContext extends ProcessedContext {
  fileId: string;
  [key: string]: unknown;
}

export type FileContextStatus = 'idle' | 'analyzing' | 'ready' | 'error';

export interface FileContextStatusEvent {
  fileId: string;
  status: FileContextStatus;
  context?: StoredFileContext;
  error?: string;
}

export type FileContextListener = (event: FileContextStatusEvent) => void;

/** Minimal description of the file a consumer wants the context for. */
export interface FileContextRequest {
  fileId: string;
  fileName?: string;
  fileType?: string;
}

/**
 * `FileProcessingService` swallows its own errors and returns this shape instead of
 * throwing. Treat it as a failure rather than a result.
 */
function isDegradedStub(context: ProcessedContext | null | undefined): boolean {
  return !!context && context.processingMethod === 'fallback' && !!context.error;
}

export class FileContextService {
  /** Resolved contexts, keyed by fileId. */
  private cache = new Map<string, StoredFileContext>();
  /** Analyses currently running, keyed by fileId — the concurrency guard. */
  private inFlight = new Map<string, Promise<StoredFileContext>>();
  /** Last known status per fileId, for UI that mounts mid-analysis. */
  private statuses = new Map<string, FileContextStatus>();
  private listeners = new Set<FileContextListener>();

  /** Subscribe to status transitions. Returns an unsubscribe function. */
  subscribe(listener: FileContextListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private emit(event: FileContextStatusEvent): void {
    this.statuses.set(event.fileId, event.status);
    this.listeners.forEach((listener) => {
      try {
        listener(event);
      } catch (err) {
        logger.error('[FileContextService] listener threw:', err);
      }
    });
  }

  getStatus(fileId: string): FileContextStatus {
    return this.statuses.get(fileId) || (this.cache.has(fileId) ? 'ready' : 'idle');
  }

  /** Synchronous read — returns undefined if the file has never been analysed. */
  getCached(fileId: string): StoredFileContext | undefined {
    return this.cache.get(fileId);
  }

  /** True while an analysis for this file is running. */
  isAnalyzing(fileId: string): boolean {
    return this.inFlight.has(fileId);
  }

  /**
   * Seed the cache with contexts already loaded from storage (e.g. FilePanel's
   * `listContexts()` on mount) so a consumer never re-pays for them.
   */
  primeCache(contexts: Array<Partial<StoredFileContext> & { fileId?: string }>): void {
    contexts.forEach((context) => {
      if (context && context.fileId && !this.cache.has(context.fileId)) {
        this.cache.set(context.fileId, context as StoredFileContext);
        this.statuses.set(context.fileId, 'ready');
      }
    });
  }

  /** Forget a file (used on delete, and by tests). Omit the id to clear everything. */
  clear(fileId?: string): void {
    if (fileId) {
      this.cache.delete(fileId);
      this.inFlight.delete(fileId);
      this.statuses.delete(fileId);
      return;
    }
    this.cache.clear();
    this.inFlight.clear();
    this.statuses.clear();
  }

  /**
   * Get the AI context for a file, analysing it only if nobody has yet.
   *
   * Resolves with the context. Rejects if the analysis failed — callers are expected
   * to surface that to the user rather than silently continuing with a stub.
   */
  ensureContext(request: FileContextRequest): Promise<StoredFileContext> {
    const { fileId } = request;
    if (!fileId) {
      return Promise.reject(new Error('ensureContext requires a fileId'));
    }

    const cached = this.cache.get(fileId);
    if (cached) return Promise.resolve(cached);

    // Registered synchronously below, so two callers in the same tick share one call.
    const running = this.inFlight.get(fileId);
    if (running) return running;

    const pending = this.runAnalysis(request);
    this.inFlight.set(fileId, pending);
    this.emit({ fileId, status: 'analyzing' });

    return pending
      .then((context) => {
        this.cache.set(fileId, context);
        this.inFlight.delete(fileId);
        this.emit({ fileId, status: 'ready', context });
        return context;
      })
      .catch((error: unknown) => {
        // Do not cache failures: the user must be able to retry.
        this.inFlight.delete(fileId);
        const message = error instanceof Error ? error.message : String(error);
        this.emit({ fileId, status: 'error', error: message });
        throw error instanceof Error ? error : new Error(message);
      });
  }

  private async runAnalysis(request: FileContextRequest): Promise<StoredFileContext> {
    const { fileId, fileName } = request;

    // 1. A context persisted by an earlier session costs nothing to reuse.
    try {
      const persisted = await fileStorageService.getFileContext(fileId);
      if (persisted && !isDegradedStub(persisted)) {
        return { ...persisted, fileId } as StoredFileContext;
      }
    } catch (err) {
      logger.warn('[FileContextService] Could not read persisted context:', err);
    }

    // 2. Otherwise analyse — exactly once, because of the in-flight guard.
    logger.debug('[FileContextService] Analysing on demand:', fileId, fileName);
    const file = await fileStorageService.getFileData(fileId);
    const context = await fileProcessingService.extractContext(file);

    if (isDegradedStub(context)) {
      throw new Error(context.error || `Could not analyse ${fileName || fileId}`);
    }

    const stored: StoredFileContext = { ...context, fileId };

    try {
      await fileStorageService.saveFileContext(fileId, stored);
    } catch (err) {
      // A persistence failure is not an analysis failure — keep the result in memory.
      logger.warn('[FileContextService] Failed to persist context:', err);
    }

    return stored;
  }
}

export const fileContextService = new FileContextService();
export default fileContextService;
